(() => {
  if (customElements.get('pe-product-carousel')) return;
  class PeProductCarousel extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.track = this.querySelector('[data-track]');
      this.pagination = this.querySelector('[data-pagination]');
      this.footer = this.querySelector('.pe-products__footer');
      if (!this.track || !this.pagination) return;
      this.slides = [...this.track.children];
      this.page = 0;
      const signal = this.abort.signal;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.update());
      }, { passive: true, signal });
      this.pagination.addEventListener('click', event => {
        const button = event.target.closest('[data-page]');
        if (button && button.getAttribute('aria-current') !== 'true') this.go(Number(button.dataset.page));
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.target !== this.track && !event.target.matches('[data-page]')) return;
        let page;
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') page = this.page + ((event.key === 'ArrowLeft') === rtl ? 1 : -1);
        if (event.key === 'Home') page = 0;
        if (event.key === 'End') page = this.pageCount - 1;
        if (page === undefined) return;
        event.preventDefault();
        this.go(page);
      }, { signal });
      this.track.addEventListener('focusin', event => {
        const slide = event.target.closest('[data-product-page]');
        if (!slide) return;
        const rect = slide.getBoundingClientRect();
        const bounds = this.track.getBoundingClientRect();
        if (rect.right <= bounds.left || rect.left >= bounds.right) this.go(Number(slide.dataset.productPage));
      }, { signal });
      this.resize = new ResizeObserver(() => {
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(() => this.layout());
      });
      this.resize.observe(this.track);
      this.layout();
    }
    layout(force = false) {
      const css = getComputedStyle(this.track);
      const requested = Math.max(1, parseFloat(css.getPropertyValue('--pm-page-size')) || 4);
      const visible = Math.min(requested, Math.max(1, this.slides.length));
      const step = Math.max(1, Math.floor(visible));
      const width = this.track.clientWidth;
      const key = `${visible}:${this.slides.length}:${width}`;
      if (!force && this.layoutKey === key) { this.update(); return; }
      const anchor = (this.page || 0) * (this.step || step);
      this.layoutKey = key;
      this.size = visible;
      this.step = step;
      this.pageCount = Math.ceil(this.slides.length / visible);
      this.track.style.setProperty('--pm-visible-columns', visible);
      this.track.style.gridTemplateColumns = `repeat(${Math.max(1, this.slides.length)}, calc((100% - ${visible - 1} * var(--pm-current-gap)) / ${visible}))`;
      this.track.style.gridTemplateRows = 'auto';
      this.slides.forEach((slide, index) => {
        slide.style.gridColumn = index + 1;
        slide.style.gridRow = 1;
        slide.dataset.productPage = Math.min(this.pageCount - 1, Math.floor(index / step));
        slide.style.scrollSnapAlign = index % step === 0 ? 'start' : 'none';
      });
      const first = this.slides[0]?.getBoundingClientRect();
      const second = this.slides[1]?.getBoundingClientRect();
      const stride = second ? Math.abs(second.left - first.left) : 0;
      const maximum = Math.max(0, this.track.scrollWidth - width);
      this.offsets = Array.from({ length: this.pageCount }, (_, page) => page === this.pageCount - 1 ? maximum : Math.min(page * step * stride, maximum));
      if (this.slides.length) this.slides[this.slides.length - 1].style.scrollSnapAlign = 'end';
      const fragment = document.createDocumentFragment();
      for (let page = 0; page < this.pageCount; page++) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pe-pagination__dot';
        button.dataset.page = page;
        button.setAttribute('aria-label', `${this.dataset.pageLabel || 'Page'} ${page + 1} / ${this.pageCount}`);
        button.setAttribute('aria-controls', this.track.id);
        fragment.append(button);
      }
      this.pagination.replaceChildren(fragment);
      this.footer.hidden = this.pageCount < 2 || this.dataset.pagination === 'false';
      this.go(Math.floor(anchor / step), true);
    }
    go(page, instant = false) {
      this.page = Math.max(0, Math.min(page, this.pageCount - 1));
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const offset = this.offsets[this.page] || 0;
      this.track.scrollTo({ left: rtl ? -offset : offset, behavior: instant || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      this.markCurrent();
    }
    update() {
      if (!this.offsets) return;
      const current = Math.abs(this.track.scrollLeft);
      let distance = Infinity;
      this.offsets.forEach((offset, page) => {
        const delta = Math.abs(current - offset);
        if (delta < distance) { distance = delta; this.page = page; }
      });
      this.markCurrent();
    }
    markCurrent() {
      [...this.pagination.children].forEach((button, page) => {
        const current = page === this.page;
        button.setAttribute('aria-current', String(current));
        button.setAttribute('aria-disabled', String(current));
      });
    }
    setItems(fragment) {
      this.track.replaceChildren(fragment);
      this.slides = [...this.track.children];
      this.page = 0;
      this.layout(true);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.layoutFrame);
    }
  }
  customElements.define('pe-product-carousel', PeProductCarousel);
})();
